<app-funnel-shell>
  <div class="funnel-card checkout">
    @switch (mode()) {

      @case ('redirect') {
        <p class="funnel-card__intro">{{ 'funnel.checkout.redirecting' | translate }}</p>
      }

      @case ('forwarding') {
        <p class="funnel-card__intro">{{ 'funnel.checkout.forwarding' | translate }}</p>
      }

      <!-- DNA Family SPECIAL asked for without an invitation link. -->
      @case ('family_missing') {
        <h1>{{ 'gh_product.dna_family_special.name' | translate }}</h1>
        <p class="funnel-card__intro">{{ 'family_join.errors.family_invitation_required' | translate }}</p>
        <a class="checkout__back" href="/">{{ 'funnel.intent.back_to_website' | translate }}</a>
      }

      <!-- Dev server only: no plan in the link and no static website at `/`. -->
      @case ('choose') {
        <h1>{{ 'funnel.checkout.choose_title' | translate }}</h1>
        <div class="checkout__choices">
          <button cButton color="primary" class="w-100" type="button" (click)="chooseProduct('membership')">
            {{ 'gh_product.membership.name' | translate }}
          </button>
          <button cButton color="primary" class="w-100" type="button" (click)="chooseProduct('practitioner')">
            {{ 'gh_product.practitioner.name' | translate }}
          </button>
          <button cButton color="primary" class="w-100" type="button" (click)="chooseProduct('dna_health_special')">
            {{ 'gh_product.dna_health_special.name' | translate }}
          </button>
        </div>
      }

      @case ('existing') {
        <h1>{{ 'funnel.checkout.existing.title' | translate }}</h1>
        <p class="funnel-card__intro">
          {{ (hasPassword() ? 'funnel.checkout.existing.body_password' : 'funnel.checkout.existing.body_link') | translate:{ email: email } }}
        </p>
        <div class="checkout__actions">
          <button cButton color="primary" class="w-100" type="button" (click)="signIn()">
            {{ 'funnel.checkout.existing.sign_in' | translate }}
          </button>
          <button class="checkout__link" type="button" (click)="backToForm()">
            {{ 'funnel.checkout.existing.other_email' | translate }}
          </button>
        </div>
      }

      @case ('checkout') {
        <h1>{{ 'funnel.checkout.title' | translate }}</h1>
        <p class="funnel-card__intro">{{ 'funnel.checkout.subtitle' | translate }}</p>

        <!-- ONE product. Its duration is an option of that product, never a second product. -->
        <section class="checkout__product">
          <div class="checkout__product-name">{{ ('gh_product.' + product() + '.name') | translate }}</div>
          <p class="checkout__product-text">{{ ('gh_product.' + product() + '.tagline') | translate }}</p>

          @if (product() === 'dna_health_special' || product() === 'dna_family_special') {
            <!-- The central promise before the component list (customer, 2026-11-03). -->
            <p class="checkout__product-promise fw-semibold">{{ 'gh_product.dna_health_special.promise' | translate }}</p>
            <ul class="checkout__components">
              @for (part of ['dna', 'nutrition', 'fitness', 'dr_future', 'membership']; track part) {
                <li>
                  <strong>{{ ('gh_product.dna_health_special.components.' + part + '.title') | translate }}</strong>
                  <span>{{ ('gh_product.dna_health_special.components.' + part + '.text') | translate }}</span>
                </li>
              }
            </ul>
            @if (isFamily()) {
              <!-- No guarantee line: the family package carries none, and says so. -->
              <p class="checkout__no-guarantee">{{ 'gh_product.dna_family_special.no_guarantee' | translate }}</p>
              <p class="funnel-card__footnote">{{ 'membership.bundle.contracting_party' | translate }}</p>
            } @else {
              <p class="checkout__guarantee">{{ 'gh_product.dna_health_special.guarantee' | translate }}</p>
            }
          } @else {
            <div class="checkout__options" role="radiogroup" [attr.aria-label]="'funnel.checkout.duration' | translate">
              <label class="checkout__option" [class.checkout__option--on]="rhythm() === 'week'">
                <input type="radio" name="rhythm" [checked]="rhythm() === 'week'" (change)="setRhythm('week')" />
                <span>{{ 'funnel.checkout.option_weeks' | translate:{ price: (weekPrice() | number:'1.2-2') } }}</span>
              </label>
              @if (rhythm() === 'week') {
                <label class="checkout__weeks">
                  <span>{{ 'funnel.checkout.weeks_label' | translate }}</span>
                  <select name="weeks" [ngModel]="weeks()" (ngModelChange)="setWeeks($event)">
                    @for (w of weekOptions; track w) {
                      <option [ngValue]="w">{{ w }}</option>
                    }
                  </select>
                </label>
              }
              <label class="checkout__option" [class.checkout__option--on]="rhythm() === 'year'">
                <input type="radio" name="rhythm" [checked]="rhythm() === 'year'" (change)="setRhythm('year')" />
                <span>{{ 'funnel.checkout.option_year' | translate:{ price: (yearPrice() | number:'1.0-2') } }}</span>
              </label>
            </div>
          }

          <div class="checkout__summary">
            <span>{{ label() }}</span>
            <strong>${{ total() | number:'1.2-2' }}</strong>
          </div>
          @if (paymentTestMode) {
            <p class="funnel-card__footnote">{{ 'funnel.checkout.test_price_note' | translate }}</p>
          }
        </section>

        <form (ngSubmit)="submit()" (focusin)="onFormFocus()" novalidate>
          <h2 class="checkout__heading">{{ 'funnel.checkout.details_title' | translate }}</h2>
          <input class="form-control mb-2" type="text" name="first_name" autocomplete="given-name"
                 [placeholder]="'funnel.landing.first_name_placeholder' | translate"
                 [(ngModel)]="firstName" (ngModelChange)="onInput()" />
          <input class="form-control mb-2" type="text" name="last_name" autocomplete="family-name"
                 [placeholder]="'funnel.landing.last_name_placeholder' | translate"
                 [(ngModel)]="lastName" (ngModelChange)="onInput()" />
          <input class="form-control mb-1" type="email" name="email" autocomplete="email"
                 [placeholder]="'funnel.landing.email_placeholder' | translate"
                 [(ngModel)]="email" (ngModelChange)="onInput()" />
          <p class="funnel-card__footnote mt-1">{{ 'funnel.checkout.account_note' | translate }}</p>

          <h2 class="checkout__heading">{{ 'funnel.checkout.legal_title' | translate }}</h2>
          @if (documentsLoading()) {
            <p class="checkout__muted">{{ 'funnel.checkout.legal_loading' | translate }}</p>
          } @else if (documentsError()) {
            <p class="text-danger small">
              {{ 'funnel.checkout.legal_error' | translate }}
              <button class="checkout__link" type="button" (click)="loadDocuments()">{{ 'funnel.checkout.retry' | translate }}</button>
            </p>
          } @else {
            @for (doc of documents(); track doc.key) {
              <div class="checkout__consent" [class.checkout__consent--missing]="missing().includes(doc.key)">
                <input type="checkbox" [id]="'consent-' + doc.key" [checked]="!!checked()[doc.key]"
                       (change)="toggle(doc.key, $any($event.target).checked)" />
                <label [for]="'consent-' + doc.key">
                  {{ doc.checkbox | translate }}
                  <a href="#" (click)="openDocument(doc, $event)">{{ 'funnel.checkout.read' | translate:{ title: doc.title } }}</a>
                </label>
              </div>
            }
          }

          <div class="my-3">
            <div id="mcaptcha-widget"></div>
            @if (captchaError() && !captchaToken()) {
              <small class="text-danger">{{ 'funnel.landing.captcha_required' | translate }}</small>
            }
          </div>

          <button cButton color="primary" type="submit" class="w-100" [disabled]="!canSubmit()">
            @if (loading()) {
              {{ 'funnel.checkout.submitting' | translate }}
            } @else {
              {{ 'funnel.checkout.submit' | translate }}
            }
          </button>

          @if (error()) {
            <div class="alert alert-danger mt-3" role="alert">{{ error() }}</div>
          }
        </form>

        <a class="checkout__back" href="/">{{ 'funnel.intent.back_to_website' | translate }}</a>
      }
    }

    @if (sponsorId() && mode() !== 'redirect') {
      <p class="funnel-card__footnote">
        {{ 'funnel.landing.referred_by' | translate }}: <strong>#{{ sponsorId() }}</strong>
      </p>
    }
  </div>
</app-funnel-shell>
